import { useCounterparty } from '@api/useCounterparty'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import {
  CustValSelectEditor,
  GraviGrid,
  Horizontal,
  SelectEditor,
  Texto,
  Vertical,
  useLocalStorage,
  validateNotEmptyString,
} from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import api from '@utils/api'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { gridViewManager } from '@/utils/grid'
import { useModelConfig } from '@hooks/useModelConfig'
import type { ICellRendererParams } from 'ag-grid-community'
import { Checkbox, Select, Switch, Tag, Tooltip } from 'antd'
import { ManyTag } from '../../_SiteWide/CellTemplates/ManyTag'

const STORAGE_KEY = 'counterParty'

export function CounterParty() {
  const [selectOptions, setSelectOptions] = useState({ type: [], carrier_type: [], trailerConfigs: [] })
  const { updateSetting } = useUserSettings()
  const { value: onlyActiveCounterparties, setValue: setOnlyActiveCounterparties } = useLocalStorage(
    'only_active_counterparties_counterparty_management',
    true
  )
  const { useGetCounterpartiesQuery, useUpdateCounterpartyMutation } = useCounterparty()
  const { data: counterparties, isPending, refetch: refresh } = useGetCounterpartiesQuery()
  const rows = useMemo(
    () => (onlyActiveCounterparties ? counterparties?.rows.filter((row) => row.active) : counterparties?.rows),
    [counterparties, onlyActiveCounterparties]
  )
  const { scopes } = useSecurityContext()
  const config = useModelConfig()
  const defaultNotifications = config?.config?.order_update_notifications || []
  useEffect(() => {
    api.getCounterpartyMetadata().then(({ data }) => {
      setSelectOptions({
        types: [...data?.types],
        carrier_type: ['Other', 'Simple', 'Managed'],
        trailerConfigs: [...data?.trailer_configs, 'N/A'],
        sourcingStrategies: [...data?.sourcing_strategies, 'N/A'],
      })
    })
  }, [])

  const handleUpdate = async (data) => {
    if (data?.allow_short_loads === 'Yes') {
      data.allow_short_loads = true
    } else if (data?.allow_short_loads === 'No') {
      data.allow_short_loads = false
    }
    data.trailer_config =
      (data?.trailer_config && data.trailer_config === 'N/A') || data?.trailer_config === null
        ? null
        : data.trailer_config
    data.sourcing_strategy =
      (data?.sourcing_strategy && data.sourcing_strategy === 'N/A') || data?.sourcing_strategy === null
        ? null
        : data.sourcing_strategy
    data.order_notification_preferences = data?.order_notification_preferences?.includes('system default')
      ? []
      : data.order_notification_preferences?.includes('no notifications')
        ? ['no notifications']
        : data?.order_notification_preferences?.map((item) => item?.toLowerCase())
    try {
      await useUpdateCounterpartyMutation.mutateAsync(data)
    } catch (err) {}
  }

  const getRowId = useCallback((row) => row?.data?.id, [])
  const columnDefs = useMemo(
    () => getColumnDefs(selectOptions, defaultNotifications, scopes),
    [selectOptions, defaultNotifications, scopes]
  )
  const agPropOverrides = useMemo(
    () => ({
      frameworkComponents: { custValSelectEditor: CustValSelectEditor, emailCR: EmailCR },
      getRowId,
      tooltipShowDelay: 500,
    }),
    [getRowId]
  )

  return (
    <GraviGrid
      controlBarProps={{
        title: 'Counterparty Management',
        actionButtons: (
          <Horizontal gap={16} alignItems='center'>
            <UploadDownloadButtons
              downloadEP={api.downloadCounterparty}
              uploadEP={scopes?.admin?.user && api.uploadCounterparty}
              fileName='Counterparty.xlsx'
              refreshCallback={refresh}
            />
            <Horizontal gap={8} verticalCenter>
              <Texto category='label'>Only Active</Texto>
              <Switch
                checked={!!onlyActiveCounterparties}
                onChange={(checked) => setOnlyActiveCounterparties(checked)}
              />
            </Horizontal>
          </Horizontal>
        ),
      }}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      storageKey={STORAGE_KEY}
      rowData={rows || []}
      loading={isPending}
      updateEP={handleUpdate}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

const getColumnDefs = (selectOptions, defaultNotifications, scopes) => {
  return [
    {
      field: 'name',
      required: true,
      valueSetter: validateNotEmptyString,
      comparator: (_a, _b, nodeA, nodeB) => {
        const aFormat = nodeA.data?.name?.toLowerCase()
        const bFormat = nodeB.data?.name?.toLowerCase()
        const orderBool = aFormat > bFormat
        return orderBool ? 1 : -1
      },
    },
    {
      headerName: 'Status',
      field: 'active',
      filter: 'agSetColumnFilter',
      width: 140,
      minWidth: 140,
      editable: false,
      cellRenderer: ({ value, setValue }: ICellRendererParams<unknown, boolean>) => (
        <Tooltip title={`Click to ${value ? 'deactivate' : 'activate'} driver`}>
          <Horizontal gap={8} alignItems='center' justifyContent='center'>
            <Switch
              checked={!!value}
              onChange={(value) => setValue?.(value)}
              checkedChildren='Active'
              unCheckedChildren='Inactive'
            />
          </Horizontal>
        </Tooltip>
      ),
    },
    {
      field: 'types',
      headerName: 'Type',
      minWidth: 200,
      autoHeight: true,
      cellEditor: SelectEditor,
      filter: true,
      cellEditorParams: {
        options: selectOptions?.types || [],
        placeholder: 'Assign Type',
        mode: 'multiple',
      },
      cellRenderer: (params) => {
        return params.data?.types?.join(', ')
      },
    },
    {
      field: 'trailer_config',
      headerName: 'Trailer Config',
      required: true,
      filter: true,
      cellEditorPopup: true,
      cellEditor: 'agRichSelectCellEditor',
      cellEditorParams: {
        values: selectOptions.trailerConfigs,
      },
      cellRenderer: (params) => {
        if (params?.data?.trailer_config === 'N/A') {
          return ''
        }
        return params.data?.trailer_config
      },
    },
    {
      field: 'carrier_type',
      headerName: 'Carrier Type',
      required: false,
      filter: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: selectOptions.carrier_type,
      },
    },
    {
      field: 'emails',
      cellEditor: 'custValSelectEditor',
      cellRenderer: 'emailCR',
      sortable: false,
    },
    {
      field: 'sourcing_strategy',
      headerName: 'Sourcing Strategy',
      required: false,
      filter: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: selectOptions.sourcingStrategies,
      },
      cellRenderer: (params) => {
        if (params?.data?.sourcing_strategy === 'N/A') {
          return ''
        }
        return params.data?.sourcing_strategy
      },
    },
    {
      field: 'allow_short_loads',
      headerName: 'Allow Short Loads',
      required: false,
      valueGetter: (params) => (params.data?.allow_short_loads ? 'Yes' : 'No'),
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['Yes', 'No'],
      },
    },
    {
      headerName: 'Order Notification Preferences',
      field: 'order_notification_preferences',
      cellEditor: CustomSelectEditor,
      cellEditorParams: { defaultNotifications },
      cellRenderer: (params) => {
        if (!params.data?.order_notification_preferences || !params.data?.order_notification_preferences?.length)
          return 'System Default'
        return <Texto textTransform={'capitalize'}>{params.data?.order_notification_preferences?.join(', ')}</Texto>
      },
      valueSetter: (params) => {
        return (params.data.order_notification_preferences = params.newValue)
      },
    },
    {
      field: 'hold',
      headerName: 'Order Hold',
      headerTooltip:
        'If set to "Yes", new orders for this counterparty will be placed on hold; existing orders remain unchanged.',
      required: false,
      valueGetter: (params) => (params.data?.hold ? 'Yes' : 'No'),
      valueSetter: (params) => {
        params.data.hold = params.newValue === 'Yes'
        return true
      },
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['Yes', 'No'],
      },
      editable: () => scopes?.counterparty_hold ?? false,
    },
  ]
}

function EmailCR({ value }) {
  return <ManyTag tagItems={value} maxCount={1} />
}

export const CustomSelectEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState()
  const refInput = useRef(null)

  useImperativeHandle(ref, () => {
    return {
      getValue() {
        return value
      },
      isPopup() {
        return true
      },
      isCancelBeforeStart() {
        return false
      },
      isCancelAfterEnd() {
        return false
      },
    }
  })
  const optionsList = [
    'system default',
    'received',
    'acknowledged',
    'in progress',
    'arrived at terminal',
    'delivered',
    'no notifications',
  ]

  const defaultDescription = useMemo(() => {
    if (props.defaultNotifications) {
      const itemsList = optionsList.filter((item) => props.defaultNotifications[item.replaceAll(' ', '_')] === true)
      return `Default notifications: ${itemsList.join(', ')}`
    }
  }, [props.defaultNotifications])
  const notificationsList = [
    { name: 'System Default', description: defaultDescription },
    { name: 'Received', description: 'A new order request is created.' },
    { name: 'Acknowledged', description: 'A request becomes an order.' },
    { name: 'In Progress', description: 'A driver begins the order.' },
    {
      name: 'Arrived At Terminal',
      description: 'The driver arrives at the last terminal before the site.',
    },
    { name: 'Delivered', description: 'The driver saves a drop for the site.' },
    { name: 'Canceled', description: 'An order tied to an order request is canceled.' },
    { name: 'No Notifications', description: 'No notifications will be sent.' },
  ]
  const notificationOptions = notificationsList.map((item) => ({
    display: (
      <Horizontal key={item.name} className='m-0 p-0'>
        <Checkbox checked={value && value.length && value.includes(item.name.toLowerCase())} />
        <Vertical
          key={item.name}
          value={item.name}
          className={'m-0 p-0 ml-2'}
          style={{ whiteSpace: 'normal', overflow: 'auto' }}
        >
          <Texto>{item.name}</Texto>
          <Texto style={{ whiteSpace: 'normal' }}>{item.description}</Texto>
        </Vertical>
      </Horizontal>
    ),
    title: item.name,
    value: item.name.toLowerCase(),
  }))

  const handleChange = (newValues) => {
    const currentValues = value
    if (newValues.includes('no notifications') && !currentValues?.includes('no notifications')) {
      setValue(['no notifications'])
      return
    }
    if (newValues.includes('system default') && !currentValues?.includes('system default')) {
      // check the object props.defaultNotifications.  it has keys that match the name of the notifications.  true means it should be included in the value array
      const itemsList = optionsList.filter((item) => props.defaultNotifications[item.replaceAll(' ', '_')] === true)
      setValue([...itemsList, 'system default'])
      // handle display value in select box
      return
    }
    const defaultItemsList = optionsList.filter(
      (item) => props.defaultNotifications[item.replaceAll(' ', '_')] === true
    )
    const valuesAreDefault =
      newValues.every((item) => defaultItemsList.includes(item)) && newValues?.length === defaultItemsList.length

    if (valuesAreDefault && !currentValues?.includes('system default')) {
      setValue(newValues.concat('system default'))
      return
    }
    if (newValues.includes('system default') && currentValues?.includes('system default')) {
      const selectedValue = newValues.filter((item) => item !== 'system default')?.[0]
      if (defaultItemsList.includes(selectedValue)) {
        setValue(currentValues.filter((item) => item !== selectedValue && item !== 'system default'))
      } else {
        // value should be added, unselect default
        setValue(currentValues.filter((item) => item !== 'system default').concat(selectedValue))
      }
      return
    }
    setValue(newValues?.filter((item) => item !== 'no notifications'))
  }
  useEffect(() => {
    if (props.value && props.value.length === 0) {
      handleChange(['system default'])
    } else {
      handleChange(props.value)
    }
  }, [])

  const tagRender = (props) => {
    const { value, closable, onClose } = props
    const onPreventMouseDown = (event) => {
      event.preventDefault()
      event.stopPropagation()
    }
    const split = value?.split(' ')
    if (split?.length) {
      split.forEach((item, index, array) => {
        array[index] = item.charAt(0).toUpperCase() + item.slice(1)
      })
    }

    return (
      <Tag onMouseDown={onPreventMouseDown} closable={closable} onClose={onClose} style={{ marginInlineEnd: 4 }}>
        {split?.join(' ') ?? ''}
      </Tag>
    )
  }
  return (
    <Horizontal alignItems='center'>
      <Select
        style={{ minWidth: 300 }}
        mode={'multiple'}
        allowClear
        onChange={handleChange}
        defaultOpen
        ref={refInput}
        defaultValue={value && value.length && value.includes('system default') ? ['system default'] : value}
        value={value && value.length && value.includes('system default') ? ['system default'] : value}
        autoFocus
        onClear={() => setValue([])}
        menuItemSelectedIcon={null}
        listHeight={400}
        tagRender={tagRender}
      >
        {notificationOptions.map((item) => (
          <Select.Option key={item.title} value={item.value}>
            {item.display}
          </Select.Option>
        ))}
      </Select>
    </Horizontal>
  )
})
